import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Building2, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";

const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];

export default function HotelLoginPage() {
    const { login, verify2FA } = useAuth() || {};
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [step, setStep] = useState(1);
    const [pendingToken, setPendingToken] = useState("");
    const [code, setCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);

    const goNext = (user) => {
        if (!HOTEL_ROLES.includes(user.role)) {
            // Admin/staff still get redirected to admin
            if (["super_admin", "admin", "operations", "finance", "content"].includes(user.role)) {
                navigate("/admin");
                return;
            }
            setError("Bu giriş sadece otel personeli içindir. Müşteri girişi için ana sayfaya dönün.");
            return;
        }
        const next = location.state?.from || "/h/dashboard";
        navigate(next);
    };

    const onSubmitCreds = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await login(email, password);
            if (res.requires_2fa) {
                setPendingToken(res.pending_token);
                setStep(2);
                setLoading(false);
                return;
            }
            goNext(res);
        } catch (err) {
            setError(err.response?.data?.detail || "Giriş başarısız");
        } finally {
            setLoading(false);
        }
    };

    const onSubmit2FA = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await verify2FA(pendingToken, code);
            goNext(user);
        } catch (err) {
            setError(err.response?.data?.detail || "Doğrulama başarısız");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="hotel-login-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 px-4 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" aria-hidden />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl" aria-hidden />

            <div className="w-full max-w-md relative z-10">
                {step === 1 ? (
                    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-emerald-200 hover:text-white mb-6 transition-colors" data-testid="hotel-back-to-site">
                        <ArrowLeft className="h-4 w-4" /> Siteye dön
                    </Link>
                ) : (
                    <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); setPendingToken(""); }} className="inline-flex items-center gap-1.5 text-sm text-emerald-200 hover:text-white mb-6 transition-colors" data-testid="hotel-2fa-back">
                        <ArrowLeft className="h-4 w-4" /> Geri
                    </button>
                )}

                <div className="bg-slate-800/40 backdrop-blur-xl rounded-2xl border border-emerald-500/20 shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                            {step === 1 ? <Building2 className="h-6 w-6 text-white" /> : <KeyRound className="h-6 w-6 text-white" />}
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-white">{step === 1 ? "Otel Girişi" : "İki Adımlı Doğrulama"}</h1>
                            <p className="text-xs text-emerald-200/80">{step === 1 ? "Otel yönetim paneline erişim" : useBackup ? "Yedek kodla giriş" : "Authenticator app'inizden 6 haneli kodu girin"}</p>
                        </div>
                    </div>

                    {step === 1 ? (
                        <form onSubmit={onSubmitCreds} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-emerald-200/70 mb-1.5">Otel E-posta</label>
                                <input
                                    data-testid="hotel-login-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="username"
                                    placeholder="manager@oteliniz.com"
                                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900/60 border border-emerald-500/20 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 text-sm font-medium text-white placeholder:text-slate-500 outline-none transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-emerald-200/70 mb-1.5">Şifre</label>
                                <input
                                    data-testid="hotel-login-password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900/60 border border-emerald-500/20 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 text-sm font-medium text-white outline-none transition-colors"
                                />
                            </div>
                            {error && (
                                <div data-testid="hotel-login-error" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                                    {error}
                                </div>
                            )}
                            <button
                                data-testid="hotel-login-submit"
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold shadow-lg shadow-emerald-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? "Giriş yapılıyor..." : "Otel Paneline Giriş"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={onSubmit2FA} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-emerald-200/70 mb-1.5">
                                    {useBackup ? "Yedek Kod" : "6 Haneli Kod"}
                                </label>
                                <input
                                    data-testid="hotel-2fa-code"
                                    type="text"
                                    required
                                    autoFocus
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder={useBackup ? "XXXX-XXXX" : "123456"}
                                    inputMode={useBackup ? "text" : "numeric"}
                                    maxLength={useBackup ? 9 : 6}
                                    className="w-full px-3.5 py-3 rounded-xl bg-slate-900/60 border border-emerald-500/20 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 text-2xl font-mono font-bold tracking-[0.4em] text-white text-center outline-none transition-colors"
                                />
                            </div>
                            {error && (
                                <div data-testid="hotel-2fa-error" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                                    {error}
                                </div>
                            )}
                            <button data-testid="hotel-2fa-submit" type="submit" disabled={loading || !code} className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold shadow-lg shadow-emerald-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                                {loading ? "Doğrulanıyor..." : "Doğrula"}
                            </button>
                            <button type="button" data-testid="hotel-2fa-toggle-backup" onClick={() => { setUseBackup((v) => !v); setCode(""); setError(""); }} className="w-full text-xs text-emerald-300 hover:text-emerald-200 transition-colors">
                                {useBackup ? "← Authenticator koduyla giriş yap" : "Authenticator'a erişemiyorum, yedek kod kullan →"}
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-6 border-t border-emerald-500/10 text-center space-y-1.5">
                        <p className="text-xs text-emerald-200/60">
                            Personel/Admin misiniz? <Link to="/staff-login" className="text-emerald-300 hover:text-white font-medium" data-testid="hotel-staff-link">Personel girişine geçin</Link>
                        </p>
                        <p className="text-xs text-emerald-200/60">
                            Müşteri misiniz? <Link to="/login" className="text-emerald-300 hover:text-white font-medium" data-testid="hotel-customer-link">Müşteri girişi</Link>
                        </p>
                    </div>
                </div>

                <p className="text-center text-xs text-emerald-200/40 mt-6">
                    Bu alan sadece otel yetkilileri içindir.
                </p>
            </div>
        </div>
    );
}
